<script setup>
import { Head, Link } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import LeadFunnel from '@/Components/LeadFunnel.vue';
import { useCopy } from '@/composables/useCopy';

const props = defineProps({
    domain: Object, posts: Object, service: String, branding: Object,
    seoTitle: { type: String, default: '' }, metaDescription: { type: String, default: '' },
    designVariant: Object,
    copyPack: { type: Object, default: () => ({}) },
    spotImages: { type: Object, default: () => ({}) },
});

const { t, ts, spot } = useCopy(props);

const serviceLabel = props.service === 'pv' ? 'Photovoltaik' : 'Wärmepumpe';
const postImage = (post, index) => {
    if (post.featured_image) return post.featured_image.startsWith('/') || /^https?:\/\//.test(post.featured_image) ? post.featured_image : `/media/${post.featured_image}`;
    const keys = ['welcome_hero', 'welcome_feature', 'welcome_process'];
    return props.spotImages?.[keys[index % 3]] || null;
};
const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
</script>

<template>
    <Head :title="seoTitle || `Ratgeber ${serviceLabel}`">
        <meta :content="metaDescription || `Aktuelle Ratgeber-Artikel rund um ${serviceLabel}.`" name="description" />
    </Head>

    <AppLayout :domain="domain" :branding="branding" :designVariant="designVariant" :service="service" activePage="blog" heroMode>
        <section class="sv-blog-hero">
            <div class="sv-blog-hero__bg" aria-hidden="true"></div>
            <div class="sv-blog-hero__content">
                <span class="sv-blog-hero__tag">{{ t('blog_hero_tag', 'Ratgeber') }}</span>
                <h1 class="sv-blog-hero__title">{{ ts('blog_hero_title', `${serviceLabel}-Ratgeber`) }}</h1>
                <p class="sv-blog-hero__sub">{{ ts('blog_hero_sub', `Aktuelle Artikel, Tipps und Vergleiche rund um ${serviceLabel === 'Photovoltaik' ? 'Solarenergie' : 'Wärmepumpen'}.`) }}</p>
            </div>
        </section>

        <section class="sv-blog-funnel-strip">
            <div class="sv-blog-funnel-strip__inner">
                <div class="sv-funnel-wrap"><LeadFunnel :domain="domain" :service="service" :design-variant="designVariant" bare :dark-mode="false" /></div>
            </div>
        </section>

        <section class="sv-blog-grid-section">
            <div class="sv-blog-grid-section__inner">
                <div v-if="posts.data && posts.data.length" class="sv-blog-grid">
                    <Link v-for="(post, index) in posts.data" :key="post.id" :href="`/ratgeber/${post.slug}`" class="sv-blog-card">
                        <div class="sv-blog-card__img-wrap">
                            <img :src="postImage(post, index)" :alt="post.title" class="sv-blog-card__img" loading="lazy" />
                        </div>
                        <div class="sv-blog-card__body">
                            <div class="sv-blog-card__meta">
                                <time v-if="post.published_at" class="sv-blog-card__date">{{ formatDate(post.published_at) }}</time>
                                <span v-if="post.reading_time" class="sv-blog-card__reading">{{ post.reading_time }} {{ t('blog_card_minutes', 'Min.') }}</span>
                            </div>
                            <div v-if="post.tags && post.tags.length" class="sv-blog-card__tags">
                                <span v-for="tag in post.tags.slice(0, 3)" :key="tag" class="sv-blog-card__tag">{{ tag }}</span>
                            </div>
                            <h2 class="sv-blog-card__title">{{ post.title }}</h2>
                            <p v-if="post.excerpt" class="sv-blog-card__excerpt">{{ post.excerpt }}</p>
                            <span class="sv-blog-card__link">{{ t('blog_card_readmore', 'Weiterlesen ->') }}</span>
                        </div>
                    </Link>
                </div>
                <div v-else class="sv-blog-empty">
                    <p>{{ t('blog_empty', 'Noch keine Artikel verfügbar. Schau bald wieder vorbei!') }}</p>
                </div>

                <div v-if="posts.links && posts.links.length > 3" class="sv-blog-pagination">
                    <Link v-for="link in posts.links" :key="link.label" :href="link.url || '#'" class="sv-blog-pagination__link" :class="{ 'sv-blog-pagination__link--active': link.active }" v-html="link.label" />
                </div>
            </div>
        </section>

        <section class="sv-blog-cta">
            <div class="sv-blog-cta__inner">
                <h2 class="sv-blog-cta__heading">{{ ts('blog_cta_heading', `Bereit für dein ${serviceLabel}-Angebot?`) }}</h2>
                <p class="sv-blog-cta__sub">{{ t('blog_cta_sub', 'Vergleiche kostenlos Angebote von Fachbetrieben aus deiner Region.') }}</p>
                <Link href="/" class="sv-blog-cta__btn">{{ t('blog_cta_btn', 'Jetzt kostenlos Angebote erhalten') }}</Link>
            </div>
        </section>
    </AppLayout>
</template>

<style scoped>
.sv-blog-hero { position: relative; min-height: 340px; display: flex; align-items: center; background: #0a1628; overflow: hidden; }
.sv-blog-hero__bg { position: absolute; inset: 0; background: linear-gradient(135deg, #0a1628, #001d75); }
.sv-blog-hero__content { position: relative; z-index: 2; max-width: 700px; margin: 0 auto; padding: 100px 24px 48px; text-align: center; }
.sv-blog-hero__tag { display: inline-block; padding: 6px 18px; border-radius: 20px; font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: #00d782; border: 1px solid rgba(0,215,130,0.3); background: rgba(0,215,130,0.08); margin-bottom: 16px; }
.sv-blog-hero__title { font-family: 'Inter', sans-serif; font-size: clamp(32px, 5vw, 52px); font-weight: 800; color: #fff; margin-bottom: 12px; }
.sv-blog-hero__sub { font-family: 'Inter', sans-serif; font-size: 17px; color: rgba(255,255,255,0.65); }

.sv-blog-funnel-strip { padding: 32px 0; background: #f0f4f8; }
.sv-blog-funnel-strip__inner { max-width: 520px; margin: 0 auto; padding: 0 24px; }
.sv-funnel-wrap { padding: 24px; background: #fff; border: 1px solid #e2e8f0; border-radius: 16px; box-shadow: 0 8px 32px rgba(0,0,0,0.06); }

.sv-blog-grid-section { padding: 64px 0; background: #ffffff; }
.sv-blog-grid-section__inner { max-width: 1100px; margin: 0 auto; padding: 0 24px; }
.sv-blog-grid { display: grid; gap: 24px; grid-template-columns: 1fr; }
@media (min-width: 768px) { .sv-blog-grid { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 1024px) { .sv-blog-grid { grid-template-columns: repeat(3, 1fr); } }
.sv-blog-card { display: flex; flex-direction: column; border-radius: 16px; overflow: hidden; background: #0f1d32; text-decoration: none; transition: transform 250ms ease, box-shadow 250ms ease; position: relative; }
.sv-blog-card::before { content: ''; position: absolute; inset: 0; border-radius: 16px; padding: 2px; background: linear-gradient(135deg, transparent, transparent); -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); -webkit-mask-composite: xor; mask-composite: exclude; pointer-events: none; transition: background 300ms ease; z-index: 1; }
.sv-blog-card:hover::before { background: linear-gradient(135deg, #00d782, #ffd700); }
.sv-blog-card:hover { transform: translateY(-4px); box-shadow: 0 12px 40px rgba(0,215,130,0.1); }
.sv-blog-card__img-wrap { aspect-ratio: 16/9; overflow: hidden; }
.sv-blog-card__img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s ease; }
.sv-blog-card:hover .sv-blog-card__img { transform: scale(1.06); }
.sv-blog-card__body { padding: 20px; flex: 1; display: flex; flex-direction: column; }
.sv-blog-card__meta { display: flex; gap: 10px; margin-bottom: 8px; }
.sv-blog-card__date { font-family: 'Inter', sans-serif; font-size: 12px; color: rgba(255,255,255,0.4); }
.sv-blog-card__reading { font-family: 'Inter', sans-serif; font-size: 12px; color: rgba(255,255,255,0.3); }
.sv-blog-card__tags { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 10px; }
.sv-blog-card__tag { font-family: 'Inter', sans-serif; font-size: 10px; font-weight: 600; padding: 3px 10px; border-radius: 16px; background: rgba(0,215,130,0.1); color: #00d782; }
.sv-blog-card__title { font-family: 'Inter', sans-serif; font-size: 17px; font-weight: 700; color: #ffffff; line-height: 1.35; margin-bottom: 8px; }
.sv-blog-card__excerpt { font-family: 'Inter', sans-serif; font-size: 13px; color: rgba(255,255,255,0.55); line-height: 1.6; flex: 1; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.sv-blog-card__link { margin-top: 14px; font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600; color: #00d782; }
.sv-blog-empty { text-align: center; padding: 64px 0; font-family: 'Inter', sans-serif; color: #94a3b8; }
.sv-blog-pagination { display: flex; justify-content: center; gap: 6px; margin-top: 48px; }
.sv-blog-pagination__link { min-width: 40px; padding: 10px 14px; border-radius: 10px; font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 500; background: #f0f4f8; color: #64748b; text-decoration: none; text-align: center; transition: all 200ms; }
.sv-blog-pagination__link:hover { background: rgba(0,215,130,0.08); }
.sv-blog-pagination__link--active { background: #00d782; color: #1a1a1a; }

.sv-blog-cta { padding: 80px 0; background: #0a1628; }
.sv-blog-cta__inner { max-width: 700px; margin: 0 auto; padding: 0 24px; text-align: center; }
.sv-blog-cta__heading { font-family: 'Inter', sans-serif; font-size: clamp(24px, 3vw, 36px); font-weight: 800; color: #ffffff; margin-bottom: 12px; }
.sv-blog-cta__sub { font-family: 'Inter', sans-serif; font-size: 16px; color: rgba(255,255,255,0.65); margin-bottom: 24px; }
.sv-blog-cta__btn { display: inline-flex; align-items: center; padding: 14px 32px; background: #00d782; color: #1a1a1a; border-radius: 12px; font-family: 'Inter', sans-serif; font-size: 15px; font-weight: 700; text-decoration: none; transition: transform 200ms, box-shadow 200ms; }
.sv-blog-cta__btn:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,215,130,0.3); }
</style>
